import { useState, useEffect, FC } from 'react'
import { useTranslation } from 'next-i18next'

import GrainButton from '@/shared/GrainButton'
import OTPField from '@/UI/DataFields/OTPField'
import Callout from '@/shared/Callout'

import { resendTokenHook } from '@/api/orchestrator'
import {
  normalizeErrorResponse,
  getErrorTitle,
  getErrorSubtext,
  getComplexErrorItems,
} from '@/utils/errorUtil'

type Login2FAProps = {
  email: string,
  setOTPCode: (otpCode: string) => void,
  error: any,
  label: string,
}

const Login2FA :FC<Login2FAProps> = ({
  label,
  setOTPCode,
  error,
  email,
}) => {
  const { t } = useTranslation(['login', 'common'], { nsMode: 'fallback' })
  const [isResending, setIsResending] = useState(false)
  const [timeLeft, setTimeLeft] = useState(10)

  const {
    mutate: resendToken,
  } = resendTokenHook()

  const onResendCode = () => {
    setIsResending(true)
    resendToken({ email })
  }

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (isResending) {
      const timer = setTimeout(() => {
        setTimeLeft(timeLeft - 1)
      }, 1000)

      if (timeLeft <= 0) {
        clearTimeout(timer)
        setIsResending(false)
      }

      return () => {
        clearTimeout(timer)
      }
    }

    if (!isResending && timeLeft === 0) {
      setTimeLeft(10)
    }
  }, [timeLeft, isResending])

  const normalizedError = error ? normalizeErrorResponse(error) : null

  return (
    <div className="twofa-step flex flex-col gap-6 [&_.otp-field_input]:!h-[56px] [&_.otp-field_input]:max-w-[72px]">
      <p>{ label }</p>
      <OTPField
        length={6}
        isNumberInput
        autoFocus
        onChangeOTP={setOTPCode}
      />

      { normalizedError && (
        <Callout
          title={getErrorTitle(normalizedError)}
          smallCaption={getErrorSubtext(normalizedError)}
          errorItems={getComplexErrorItems(normalizedError)}
          isError
        />
      )}

      <div className="divider grow border-t border-t-gray-5" />

      <div className="non-field-action">
        <div className="caption-text mb-4 text-gray-3">{t('didnt_receive_it')}</div>
        <GrainButton
          text={isResending ? t('code_resent_resend_in', { seconds: timeLeft }) : t('resend_code')}
          variant="secondary"
          onClick={onResendCode}
          isDisabled={isResending}
        />
      </div>
    </div>
  )
}

export default Login2FA
